// Mercapoli - Admin Finanzas Avanzado (Fase 3)
// Dashboard Financiero con IA, Multi-filtro, Desglose por Comercio/Rider/Categoría
// GO-LIVE: Configuración de país dinámico (NO hardcodeado)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, DollarSign, Loader2, TrendingUp, TrendingDown,
  Wallet, Store, Bike, CreditCard, CheckCircle, AlertTriangle,
  FileSpreadsheet, FileText, Download, RefreshCw, Globe, MapPin,
  Sparkles, Lightbulb, BarChart3, PieChart as PieChartIcon,
  Calendar, Filter, Building, Tag, ChevronDown, ChevronUp,
  ArrowUpRight, ArrowDownRight, Minus, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { admin as adminApi, config as configApi } from '../../lib/api';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// País por defecto (CONFIGURABLE via .env - NO hardcodear ningún país)
const DEFAULT_COUNTRY = process.env.REACT_APP_DEFAULT_COUNTRY || 'GT';

// Mapa de países para UI (debe coincidir con backend PAISES_CONFIG)
const PAISES_MAP = {
  'GT': { nombre: 'Guatemala', emoji: '🇬🇹' },
  'MX': { nombre: 'México', emoji: '🇲🇽' },
  'CO': { nombre: 'Colombia', emoji: '🇨🇴' },
  'PE': { nombre: 'Perú', emoji: '🇵🇪' },
  'CL': { nombre: 'Chile', emoji: '🇨🇱' },
  'AR': { nombre: 'Argentina', emoji: '🇦🇷' },
  'US': { nombre: 'Estados Unidos', emoji: '🇺🇸' },
  'GLOBAL': { nombre: 'Vista Global', emoji: '🌍' },
};

const AdminFinanzas = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filtros - Default: país configurado en .env (NO hardcodeado)
  const [countryFilter, setCountryFilter] = useState(DEFAULT_COUNTRY);
  const [periodFilter, setPeriodFilter] = useState('month');
  const [cityFilter, setCityFilter] = useState('');
  const [merchantFilter, setMerchantFilter] = useState('');
  
  // Datos
  const [financeData, setFinanceData] = useState(null);
  const [balanceRepartidores, setBalanceRepartidores] = useState([]);
  const [balanceRestaurantes, setBalanceRestaurantes] = useState([]);
  
  // UI State
  const [expandedSection, setExpandedSection] = useState('overview');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [pagoTipo, setPagoTipo] = useState(null);
  const [pagoEntidad, setPagoEntidad] = useState(null);
  const [pagoData, setPagoData] = useState({
    monto: 0,
    metodo: 'transferencia',
    referencia: '',
    notas: ''
  });
  const [procesandoPago, setProcesandoPago] = useState(false);

  useEffect(() => {
    loadData();
  }, [countryFilter, periodFilter, cityFilter, merchantFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Calcular fechas según periodo
      const now = new Date();
      let fromDate = new Date();
      
      switch (periodFilter) {
        case 'today':
          fromDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          fromDate.setDate(fromDate.getDate() - 7);
          break;
        case 'month':
          fromDate.setMonth(fromDate.getMonth() - 1);
          break;
        case 'year':
          fromDate.setFullYear(fromDate.getFullYear() - 1);
          break;
        default:
          fromDate.setMonth(fromDate.getMonth() - 1);
      }
      
      const params = {
        country_code: countryFilter,
        from_date: fromDate.toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0]
      };
      
      if (cityFilter) params.city_id = cityFilter;
      if (merchantFilter) params.merchant_id = merchantFilter;
      
      const [finData, repartidoresData, restaurantesData] = await Promise.all([
        adminApi.getFinanceDashboard(params),
        adminApi.getBalanceRepartidores(),
        adminApi.getBalanceRestaurantes()
      ]);
      
      setFinanceData(finData);
      setBalanceRepartidores(repartidoresData);
      setBalanceRestaurantes(restaurantesData);
    } catch (error) {
      console.error('Error loading finance data:', error);
      toast.error('Error al cargar datos financieros');
    }
    setLoading(false);
    setRefreshing(false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const abrirModalPago = (tipo, entidad) => {
    setPagoTipo(tipo);
    setPagoEntidad(entidad);
    setPagoData({
      monto: tipo === 'repartidor' ? entidad.pendiente : entidad.payout_pendiente,
      metodo: 'transferencia',
      referencia: '',
      notas: ''
    });
    setShowPagoModal(true);
  };

  const procesarPago = async () => {
    if (!pagoData.monto || pagoData.monto <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    
    setProcesandoPago(true);
    try {
      if (pagoTipo === 'repartidor') {
        await adminApi.pagarRepartidor({
          repartidor_id: pagoEntidad.user_id,
          ...pagoData
        });
      } else {
        await adminApi.pagarRestaurante({
          restaurante_id: pagoEntidad.restaurante_id,
          ...pagoData
        });
      }
      
      toast.success('Pago registrado exitosamente');
      setShowPagoModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar pago');
    }
    setProcesandoPago(false);
  };

  const formatCurrency = (value, symbol = '$') => {
    return `${symbol}${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'risk': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'opportunity': return <Lightbulb className="h-5 w-5 text-blue-500" />;
      case 'trend': return <TrendingUp className="h-5 w-5 text-purple-500" />;
      case 'recommendation': return <Sparkles className="h-5 w-5 text-green-500" />;
      default: return <BarChart3 className="h-5 w-5 text-gray-500" />;
    }
  };

  const getInsightBg = (type) => {
    switch (type) {
      case 'alert': return 'bg-red-50 border-red-200';
      case 'risk': return 'bg-amber-50 border-amber-200';
      case 'opportunity': return 'bg-blue-50 border-blue-200';
      case 'trend': return 'bg-purple-50 border-purple-200';
      case 'recommendation': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const currency = financeData?.currency || { symbol: '$', code: 'USD' };
  const metrics = financeData?.main_metrics || {};
  const cashflow = financeData?.cashflow || {};
  const aiInsights = financeData?.ai_insights?.insights || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="font-heading text-lg font-bold">Finanzas Avanzado</h1>
                <p className="text-xs text-muted-foreground">Dashboard Financiero + IA</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="export-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => {
                      const url = adminApi.exportFinanceExcel({ country_code: countryFilter });
                      window.open(url, '_blank');
                      toast.success('Descargando Excel...');
                    }}
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                    Exportar a Excel
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => {
                      const url = adminApi.exportFinancePdf({ country_code: countryFilter });
                      window.open(url, '_blank');
                      toast.success('Descargando PDF...');
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2 text-red-600" />
                    Exportar a PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="container-app py-6 space-y-6">
        {/* Banner de País Activo - Consistente con Dashboard */}
        <div className="bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 border border-brand-primary/20 rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-brand-primary/20 flex items-center justify-center text-xl">
                {PAISES_MAP[countryFilter]?.emoji || '🌍'}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Finanzas de</p>
                <p className="font-bold text-lg">
                  {PAISES_MAP[countryFilter]?.nombre || countryFilter}
                </p>
              </div>
              <Badge className="ml-2 bg-brand-primary text-white">
                {currency.symbol} {currency.code}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-brand-primary" />
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="w-[180px] border-brand-primary/30" data-testid="country-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GLOBAL" className="font-medium">🌍 Vista Global (USD)</SelectItem>
                    <div className="border-t my-1" />
                    <SelectItem value="GT">🇬🇹 Guatemala</SelectItem>
                    <SelectItem value="MX">🇲🇽 México</SelectItem>
                    <SelectItem value="CO">🇨🇴 Colombia</SelectItem>
                    <SelectItem value="PE">🇵🇪 Perú</SelectItem>
                    <SelectItem value="CL">🇨🇱 Chile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select value={periodFilter} onValueChange={setPeriodFilter}>
                  <SelectTrigger className="w-[130px]" data-testid="period-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Hoy</SelectItem>
                    <SelectItem value="week">Semana</SelectItem>
                    <SelectItem value="month">Mes</SelectItem>
                    <SelectItem value="year">Año</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {countryFilter === 'GLOBAL' && (
            <p className="text-xs text-muted-foreground mt-2">
              Vista global consolida datos en USD. Selecciona un país para ver métricas en moneda local.
            </p>
          )}
        </div>

        {/* KPIs Principales */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm opacity-90">Ingresos Brutos</span>
              </div>
              <p className="text-2xl font-bold">
                {formatCurrency(metrics.ingresos_brutos, currency.symbol)}
              </p>
              <p className="text-sm opacity-75">{metrics.pedidos_completados} pedidos</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                <Wallet className="h-5 w-5 text-purple-500" />
                <span className="text-sm">Comisiones Mercapoli</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(metrics.comisiones_plataforma, currency.symbol)}
              </p>
              <p className="text-sm text-muted-foreground">
                {metrics.comision_efectiva_porcentaje}% efectiva
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                <Store className="h-5 w-5 text-blue-500" />
                <span className="text-sm">Payout Comercios</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(metrics.payout_comercios, currency.symbol)}
              </p>
              <p className="text-sm text-muted-foreground">
                Pendiente: {formatCurrency(metrics.payout_pendiente_comercios, currency.symbol)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-brand-dark to-brand-dark/80 text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5" />
                <span className="text-sm opacity-90">Margen Neto</span>
              </div>
              <p className="text-2xl font-bold">
                {formatCurrency(metrics.margen_neto_mercapoli, currency.symbol)}
              </p>
              <p className="text-sm opacity-75">
                Riders pendiente: {formatCurrency(metrics.payout_pendiente_riders, currency.symbol)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Segunda fila de KPIs */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className={cashflow.liquidez === 'positiva' ? 'bg-green-50' : 'bg-red-50'}>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">Cashflow Neto</p>
              <p className={`text-2xl font-bold ${cashflow.liquidez === 'positiva' ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(cashflow.cashflow_neto, currency.symbol)}
              </p>
              <Badge variant={cashflow.liquidez === 'positiva' ? 'default' : 'destructive'} className="mt-1">
                {cashflow.liquidez === 'positiva' ? '✓ Liquidez OK' : '⚠ Liquidez Negativa'}
              </Badge>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">Ticket Promedio</p>
              <p className="text-2xl font-bold">
                {formatCurrency(metrics.ticket_promedio, currency.symbol)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">Costos Envío</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(metrics.costos_envio, currency.symbol)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-muted-foreground">Payout Riders</p>
              <p className="text-2xl font-bold text-amber-600">
                {formatCurrency(metrics.payout_riders, currency.symbol)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Insights Financieros */}
        {aiInsights.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-brand-primary" />
                <CardTitle>Insights Financieros</CardTitle>
                <Badge variant="secondary" className="text-xs">IA-04</Badge>
              </div>
              <CardDescription>Análisis automático de métricas financieras</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2">
                {aiInsights.map((insight, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`p-4 rounded-lg border ${getInsightBg(insight.type)}`}
                  >
                    <div className="flex items-start gap-3">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{insight.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {insight.description}
                        </p>
                        <Badge 
                          variant={insight.priority === 'high' ? 'destructive' : 'outline'} 
                          className="mt-2 text-xs"
                        >
                          {insight.priority === 'high' ? 'Alta' : insight.priority === 'medium' ? 'Media' : 'Baja'}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs de desglose */}
        <Tabs defaultValue="merchants" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="merchants" className="flex items-center gap-2">
              <Store className="h-4 w-4" />
              Comercios
            </TabsTrigger>
            <TabsTrigger value="riders" className="flex items-center gap-2">
              <Bike className="h-4 w-4" />
              Riders
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Categorías
            </TabsTrigger>
            <TabsTrigger value="geography" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Geografía
            </TabsTrigger>
          </TabsList>

          {/* Tab Comercios */}
          <TabsContent value="merchants" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Desglose por Comercio</CardTitle>
                <CardDescription>Rentabilidad individual de cada comercio</CardDescription>
              </CardHeader>
              <CardContent>
                {financeData?.by_merchant?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Comercio</TableHead>
                          <TableHead>Ciudad</TableHead>
                          <TableHead className="text-right">Pedidos</TableHead>
                          <TableHead className="text-right">Ingresos</TableHead>
                          <TableHead className="text-right">Comisión</TableHead>
                          <TableHead className="text-right">Payout</TableHead>
                          <TableHead className="text-right">Margen %</TableHead>
                          <TableHead className="text-right">Pendiente</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financeData.by_merchant.map((m) => (
                          <TableRow key={m.restaurante_id}>
                            <TableCell className="font-medium">{m.nombre}</TableCell>
                            <TableCell>{m.ciudad}</TableCell>
                            <TableCell className="text-right">{m.pedidos}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(m.ingresos_brutos, currency.symbol)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(m.comision, currency.symbol)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(m.payout, currency.symbol)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant={m.margen_porcentaje > 15 ? 'default' : 'secondary'}>
                                {m.margen_porcentaje}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {m.payout_pendiente > 0 && (
                                <span className="text-amber-600">
                                  {formatCurrency(m.payout_pendiente, currency.symbol)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {m.payout_pendiente > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => abrirModalPago('restaurante', m)}
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Pagar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No hay datos de comercios en este periodo
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Riders */}
          <TabsContent value="riders" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Desglose por Rider</CardTitle>
                <CardDescription>Ganancias y payouts de repartidores</CardDescription>
              </CardHeader>
              <CardContent>
                {financeData?.by_rider?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rider</TableHead>
                          <TableHead>Ciudad</TableHead>
                          <TableHead className="text-right">Entregas</TableHead>
                          <TableHead className="text-right">Ganancias</TableHead>
                          <TableHead className="text-right">Pagado</TableHead>
                          <TableHead className="text-right">Pendiente</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financeData.by_rider.map((r) => (
                          <TableRow key={r.user_id}>
                            <TableCell className="font-medium">{r.nombre}</TableCell>
                            <TableCell>{r.ciudad}</TableCell>
                            <TableCell className="text-right">{r.entregas}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(r.ganancias_totales, currency.symbol)}
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {formatCurrency(r.pagado, currency.symbol)}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.pendiente > 0 && (
                                <span className="text-amber-600 font-medium">
                                  {formatCurrency(r.pendiente, currency.symbol)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {r.pendiente > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => abrirModalPago('repartidor', r)}
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Pagar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No hay datos de riders en este periodo
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Categorías */}
          <TabsContent value="categories" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Desglose por Categoría</CardTitle>
                <CardDescription>Rentabilidad por tipo de comercio</CardDescription>
              </CardHeader>
              <CardContent>
                {financeData?.by_category?.length > 0 ? (
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Categoría</TableHead>
                            <TableHead className="text-right">Comercios</TableHead>
                            <TableHead className="text-right">Pedidos</TableHead>
                            <TableHead className="text-right">Ingresos</TableHead>
                            <TableHead className="text-right">Margen %</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {financeData.by_category.map((c) => (
                            <TableRow key={c.categoria_id}>
                              <TableCell className="font-medium">{c.nombre}</TableCell>
                              <TableCell className="text-right">{c.comercios}</TableCell>
                              <TableCell className="text-right">{c.pedidos}</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(c.ingresos_brutos, currency.symbol)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant={c.margen_porcentaje > 15 ? 'default' : 'secondary'}>
                                  {c.margen_porcentaje}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={financeData.by_category.slice(0, 6)}
                            dataKey="ingresos_brutos"
                            nameKey="nombre"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`}
                          >
                            {financeData.by_category.slice(0, 6).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => [formatCurrency(value, currency.symbol), 'Ingresos']} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No hay datos de categorías en este periodo
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Geografía */}
          <TabsContent value="geography" className="mt-4">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Por País */}
              {financeData?.by_country?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      Por País
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={financeData.by_country}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="country_code" />
                          <YAxis />
                          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos USD']} />
                          <Bar dataKey="ingresos_usd" fill="#22c55e" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-2">
                      {financeData.by_country.map((country) => (
                        <div key={country.country_code} className="flex items-center justify-between text-sm">
                          <span className="font-medium">{country.country_name}</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline">{country.pedidos} pedidos</Badge>
                            <span className="font-bold">${country.ingresos_usd.toLocaleString()}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Por Ciudad */}
              {financeData?.by_city?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Por Ciudad
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {financeData.by_city.slice(0, 8).map((city, idx) => (
                        <div key={city.ciudad_id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span className="font-medium">{city.nombre}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{formatCurrency(city.ingresos, currency.symbol)}</p>
                            <p className="text-xs text-muted-foreground">
                              {city.pedidos} pedidos • Ticket: {formatCurrency(city.ticket_promedio, currency.symbol)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Top Performers */}
        {financeData?.top_performers && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {/* Top Comercios */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Store className="h-4 w-4" /> Comercios más rentables
                  </h4>
                  <div className="space-y-2">
                    {financeData.top_performers.merchants?.slice(0, 3).map((m, idx) => (
                      <div key={m.restaurante_id} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                            {idx + 1}
                          </Badge>
                          {m.nombre}
                        </span>
                        <span className="text-green-600 font-medium">{m.margen_porcentaje}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Riders */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Bike className="h-4 w-4" /> Riders más activos
                  </h4>
                  <div className="space-y-2">
                    {financeData.top_performers.riders?.slice(0, 3).map((r, idx) => (
                      <div key={r.user_id} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                            {idx + 1}
                          </Badge>
                          {r.nombre}
                        </span>
                        <span className="text-blue-600 font-medium">{r.entregas} entregas</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Categorías */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Tag className="h-4 w-4" /> Categorías con mayor margen
                  </h4>
                  <div className="space-y-2">
                    {financeData.top_performers.categories?.slice(0, 3).map((c, idx) => (
                      <div key={c.categoria_id} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                            {idx + 1}
                          </Badge>
                          {c.nombre}
                        </span>
                        <span className="text-purple-600 font-medium">{c.margen_porcentaje}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de Pago */}
      <Dialog open={showPagoModal} onOpenChange={setShowPagoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Registrar Pago - {pagoEntidad?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto ({currency.symbol})</Label>
              <Input
                type="number"
                step="0.01"
                value={pagoData.monto}
                onChange={(e) => setPagoData(prev => ({ ...prev, monto: parseFloat(e.target.value) }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Método de Pago</Label>
              <Select 
                value={pagoData.metodo} 
                onValueChange={(v) => setPagoData(prev => ({ ...prev, metodo: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transferencia">Transferencia Bancaria</SelectItem>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Referencia / No. Transacción</Label>
              <Input
                value={pagoData.referencia}
                onChange={(e) => setPagoData(prev => ({ ...prev, referencia: e.target.value }))}
                placeholder="Ej: TRF-123456"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Input
                value={pagoData.notas}
                onChange={(e) => setPagoData(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Observaciones..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPagoModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={procesarPago}
              disabled={procesandoPago}
              className="bg-brand-primary hover:bg-brand-secondary"
            >
              {procesandoPago ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFinanzas;
